import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { initializeApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  MessagePayload,
  Messaging,
} from 'firebase/messaging';

import {
  selectSession,
  // useGetPromosMutation,
  useLazyGetPropertiesQuery,
  useLogoutMutation,
  useRegisterTokenMutation,
} from '../redux';
import { NotificationData, VendamosNotification } from '../types';
import { NotificationType } from '../enums';
import { useContentBadges } from './useContentBadges';
import { PROPERTIES_URL } from '../config';

const firebaseConfig = {
  apiKey: 'AIzaSyDvizq-40midSdLXUYUvz3lY7tG-MO_F_0',
  authDomain: 'claro-promociones-d6823.firebaseapp.com',
  projectId: 'claro-promociones-d6823',
  storageBucket: 'claro-promociones-d6823.appspot.com',
  messagingSenderId: '129503756840',
  appId: '1:129503756840:web:11951eaa7e7abd2b9b2b1d',
};

const vapikey =
  'BAYUYrgPBFG-fQjtmqTqtwmITfeIf0O9ql0uezJRofd_1Nie3U5rSIYlg22ZYlW9X0BozIYBkrrmlph_UHW7s_g';

const firebaseApp = initializeApp(firebaseConfig);

/**
 * Firebase Messaging necesita `navigator.serviceWorker`, que el navegador solo
 * expone en un contexto seguro: `https`, `localhost` o `127.0.0.1`. Fuera de uno
 * —una IP de la red local, un nombre `.local`, jsdom— la propiedad no existe y
 * `getMessaging` lanza mientras se evalúa este módulo. Como el barrel de hooks
 * lo reexporta, ese fallo deja la aplicación entera sin arrancar en vez de
 * degradar solo las notificaciones.
 */
const isMessagingAvailable = (): boolean =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const resolveMessaging = (): Messaging | undefined => {
  if (!isMessagingAvailable()) return undefined;
  try {
    return getMessaging(firebaseApp);
  } catch {
    return undefined;
  }
};

const messaging = resolveMessaging();

export const useNotification = () => {
  const [token, setToken] = useState<string | null>();
  const [notifications, setNotifications] = useState<VendamosNotification[]>(
    []
  );
  const [registerFcmToken] = useRegisterTokenMutation();
  const session = useSelector(selectSession);
  // const [getPromos] = useGetPromosMutation();
  const [getProperties] = useLazyGetPropertiesQuery();
  const [logoutMutation] = useLogoutMutation();
  const { updateBadgesWithTag } = useContentBadges();

  useEffect(() => {
    if (!messaging) return;
    /**
     * Las notificaciones son accesorias: si el token no se obtiene, la
     * aplicación sigue funcionando y no hay superficie donde avisarle al
     * vendedor. Se descarta a propósito, no por descuido. El registro
     * estructurado está pendiente en docs/reference/deuda.md.
     */
    getToken(messaging, { vapidKey: vapikey })
      .then(setToken)
      .catch(() => setToken(null));
  }, []);

  const readNotification = async ({
    contentId,
    contentType,
    imageUrl,
    message,
    notificationType,
    subContentType,
    title,
  }: NotificationData) => {
    switch (notificationType) {
      case NotificationType.CONTENT_UPDATED:
        const tag = `${contentType}-${subContentType}-${contentId}`;
        // await getPromos(session);
        setNotifications(list => [
          ...list,
          {
            title,
            body: message,
            icon: imageUrl,
            image: imageUrl,
            tag,
          } as VendamosNotification,
        ]);
        await updateBadgesWithTag(tag);
        break;
      case NotificationType.CONTENT_DELETE:
        // await getPromos(session);
        break;
      case NotificationType.APP_RESET:
        // Con `{}` pedía `/[object Object]` y nunca el catálogo (VWA-43).
        await getProperties(PROPERTIES_URL);
        // await getPromos(session);
        break;
      case NotificationType.APP_UPDATE:
        localStorage.removeItem('persist:root');
        localStorage.clear();
        await logoutMutation(session.username!);
        break;
    }
  };

  const onMessageListener = (fcm: Messaging): Promise<MessagePayload> =>
    new Promise(resolve => {
      onMessage(fcm, (payload: MessagePayload) => {
        resolve(payload);
      });
    });

  const refreshNotifications = async (data: NotificationData) => {
    // await getPromos(session);
    await readNotification(data);
  };

  const listening = () => {
    if (!messaging) return;
    onMessageListener(messaging)
      .then(payload => {
        // await getPromos(session);
        // readNotification(payload.data as unknown as NotificationData);
        refreshNotifications(payload.data as unknown as NotificationData);
      })
      .catch(() => undefined)
      .finally(() => {
        listening();
      });
  };

  const updateFcmTokenInBackend = async () => {
    const { username } = session;
    registerFcmToken({ token: token!, username });
  };

  const removeNotification = (notif: VendamosNotification) => {
    setNotifications(list => list.filter(n => n !== notif));
  };

  useEffect(() => {
    if (!!token) {
      updateFcmTokenInBackend();
      listening();
    }
    // eslint-disable-next-line
  }, [token]);

  return {
    notifications,
    removeNotification,
  };
};
